import { CustomElement } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { Display, setDisplay, toggleVisible } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { FLEX_CONTAINER, FLEX_CONTAINER_COLUMN, FLEX_ITEM_AUTO, MARGIN } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { REF_ATTRIBUTE, createHtmlElement } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { CjsButton } from "@cervval.js/client-html5-ui/src/components/button/button";
import { CjsCheckbox } from "@cervval.js/client-html5-ui/src/components/checkbox/checkbox";
import { CjsDialog, DialogOptions, defaultDialogOptions } from "@cervval.js/client-html5-ui/src/components/dialog/dialog";
import { CjsIconFa } from "@cervval.js/client-html5-ui/src/components/icon/iconFa";
import { CjsInput } from "@cervval.js/client-html5-ui/src/components/inputs/input";
import * as passwordUtils from "@cervval.js/common-auth/src/password.utils";
import { ERROR_MESSAGE_SERVICE, ErrorMessageService, LOG_SERVICE, LogService } from "@cervval.js/common/services";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { ErrorWithCode } from "@cervval.js/common/src/errors/errors";
import { I18nType, i18n } from "./i18n/i18n";

export type ChangePasswordSettings = {
    minLength: number,
    minUppercase: number,
    minLowercase: number,
    minSpecial: number,
    minNumber: number,
    canChangePasswordOnNextLogin: boolean
}

type VerifyPassword = (p1: string, p2: string) => boolean;

export interface ChangePasswordOptions {
    dialogOptions: Partial<DialogOptions>;
}

export const defaultChangePasswordOptions: ChangePasswordOptions = {
    dialogOptions: defaultDialogOptions
};

@CustomElement({
    name: "cjs-change-password",
    style: require("./changePassword.component.scss"),
    template: require("./changePassword.component.html"),
    i18n
})
export class CjsChangePassword extends WebComponent<I18nType> {

    @Injected(LOG_SERVICE, { optional: true })
    protected readonly _log?: LogService;

    @Injected(ERROR_MESSAGE_SERVICE)
    protected readonly _errorMessageService!: ErrorMessageService;

    @TypedWebComponentOptions(defaultChangePasswordOptions)
    public readonly options!: WebComponentOptions<ChangePasswordOptions>;

    /** components */
    @TypedHtmlElements()
    private readonly _components!: {
        changePasswordContainer: HTMLDivElement,
        changePasswordDialogButton: CjsButton
    };

    private _validationRequirements: { passwordRequirement: HTMLDivElement, validIcon: CjsIconFa, invalidIcon: CjsIconFa, verifyMethod: VerifyPassword }[] = [];

    private _passwordInput = new CjsInput<string>({
        floatingLabel: {
            enable: true,
            text: this.i18n.firstPassword.label
        },
        placeholderText: this.i18n.firstPassword.label,
        inputType: "password"
    });

    /** Input used to check if the first password given is same as given */
    private _checkPasswordInput = new CjsInput<string>({
        floatingLabel: {
            enable: true,
            text: this.i18n.secondPassword.label
        },
        placeholderText: this.i18n.secondPassword.label,
        inputType: "password"
    });

    private _confirmButton?: CjsButton;

    constructor(
        protected _onConfirmChange: (password: string, changePasswordOnNextLogin: boolean) => Promise<void>,
        protected _settings: ChangePasswordSettings = {
            // Default parameters
            minLength: 8,
            minUppercase: 1,
            minLowercase: 1,
            minSpecial: 1,
            minNumber: 1,
            canChangePasswordOnNextLogin: false
        },
        options?: Partial<ChangePasswordOptions>
    ) {
        super();
        // prevent autofilling of passwords https://developer.mozilla.org/en-US/docs/Web/Security/Securing_your_site/Turning_off_form_autocompletion
        this._passwordInput.input.autocomplete = "new-password";
        this._checkPasswordInput.input.autocomplete = "new-password";
        this.options.setWithoutRefresh(options);
    }


    /** Init component display */
    public override init(): void {

        // Init dialog opener button
        this._components.changePasswordDialogButton.clickEvent.on(this.onDialogButtonClick.bind(this));
        this._components.changePasswordDialogButton.options.setAndRefreshIfChanged({ label: this.i18n.changePassword.innerText });
    }

    /** Refresh */
    public override refresh(): void {

        // Reset
        this._validationRequirements = [];

        // Refresh inputs placeholders from i18n
        this._passwordInput.options.setAndRefreshIfChanged({
            floatingLabel: {
                enable: true,
                text: this.i18n.firstPassword.label
            },
            placeholderText: this.i18n.firstPassword.label
        });
        this._checkPasswordInput.options.setAndRefreshIfChanged({
            floatingLabel: {
                enable: true,
                text: this.i18n.secondPassword.label
            },
            placeholderText: this.i18n.secondPassword.label
        });
    }

    /**
     * Handle click on dialog button
     */
    public onDialogButtonClick(): void {
        this._createChangePasswordDialog();
        this._confirmButton?.options.setAndRefreshIfChanged({ disabled: true });
        this._passwordInput.value = "";
        this._checkPasswordInput.value = "";

    }

    /**
     * Create Change Password Dialog
     */
    private _createChangePasswordDialog(): CjsDialog {

        const dialog = new CjsDialog({
            title: this.i18n.changePasswordTitle.title,
            footerButtons: [{
                button: {
                    label: this.i18n.confirmChange.innerHTML,
                    refAttribute: "confirmButton",
                    disabled: true
                },
                action: "confirm"
            }],
            ...this.options.dialogOptions
        });

        this._confirmButton = dialog.confirmButton;

        dialog.addToBody(this._createChangePasswordForm(dialog));

        return dialog;
    }

    /**
     * Init password requirements
     */
    private _initPasswordRequirements(requirementList: HTMLDivElement): void {
        if (this._settings.minLength > 0) {
            this._addPasswordRequirement(
                this.i18n.minLengthRequirement.innerText + ` (${this._settings.minLength})`,
                (p1, _p2) => passwordUtils.hasMinimalLength(p1, this._settings.minLength),
                requirementList
            );
        }
        if (this._settings.minUppercase > 0) {
            this._addPasswordRequirement(
                this.i18n.minUppercaseRequirement.innerText + ` (${this._settings.minUppercase})`,
                (p1, _p2) => passwordUtils.hasMinimalUppercase(p1, this._settings.minUppercase),
                requirementList
            );
        }
        if (this._settings.minLowercase > 0) {
            this._addPasswordRequirement(
                this.i18n.minLowercaseRequirement.innerText + ` (${this._settings.minLowercase})`,
                (p1, _p2) => passwordUtils.hasMinimalLowercase(p1, this._settings.minLowercase),
                requirementList
            );
        }
        if (this._settings.minSpecial > 0) {
            this._addPasswordRequirement(
                this.i18n.minSpecialRequirement.innerText + ` (${this._settings.minSpecial})`,
                (p1, _p2) => passwordUtils.hasMinimalSpecialCharacters(p1, this._settings.minSpecial),
                requirementList
            );
        }
        if (this._settings.minNumber > 0) {
            this._addPasswordRequirement(
                this.i18n.minNumberRequirement.innerText + ` (${this._settings.minNumber})`,
                (p1, _p2) => passwordUtils.hasMinimalNumbers(p1, this._settings.minNumber),
                requirementList
            );
        }
        this._addPasswordRequirement(
            this.i18n.passwordMatchRequirement.innerText,
            (p1, p2) => p1 !== "" && p1 != null && p1 === p2,
            requirementList
        );
    }

    /**
     * Create Change Password Form
     */
    private _createChangePasswordForm(dialog: CjsDialog): HTMLFormElement {
        // UI Elements
        const changePasswordForm = createHtmlElement("form", {
            attributes: {
                [REF_ATTRIBUTE]: "changePasswordForm"
            }
        });
        const tmpPasswordCheckbox = new CjsCheckbox({
            label: this.i18n.tmpPasswordLabel.label
        });

        const errorContainer = createHtmlElement("div", {
            attributes: {
                [REF_ATTRIBUTE]: "errorMessage"
            }
        });
        const requirementList = createHtmlElement("div", {
            attributes: {
                [REF_ATTRIBUTE]: "requirementList"
            }
        });

        // Refresh form on input to handle validation requirements
        this._passwordInput.inputEvent.on(() => this._arePasswordsValid(this._passwordInput.value, this._checkPasswordInput.value));
        this._checkPasswordInput.inputEvent.on(() => this._arePasswordsValid(this._passwordInput.value, this._checkPasswordInput.value));

        // Init password requirements validation
        this._initPasswordRequirements(requirementList);

        // On form submission
        if (this._confirmButton) {
            this._confirmButton.clickEvent.on(() => {
                // Inputs values
                const firstValue = this._passwordInput.value;
                const secondValue = this._checkPasswordInput.value;

                if (firstValue && secondValue) {
                    this._clearError(errorContainer);

                    this._onConfirmChange(firstValue, this._settings.canChangePasswordOnNextLogin && tmpPasswordCheckbox.getValue() === true).then(() => {
                        dialog.hide();
                    }).catch((err) => {
                        this._showError(err, errorContainer);
                    });
                }
            });
        }

        // Add elements to form
        changePasswordForm.append(this._passwordInput, requirementList, this._checkPasswordInput);

        // Display checkbox if form needs an option for user to change password on nextlogin
        if (this._settings.canChangePasswordOnNextLogin) {
            changePasswordForm.append(tmpPasswordCheckbox);
        }

        changePasswordForm.append(errorContainer);

        return changePasswordForm;
    }

    /**
     * Will add a new line of requirement for the new password
     * @param requirementExplaination Explaination of the requirement
     * @param verifyMethod Method to verify that the requirement is met 
     */
    private _addPasswordRequirement(requirementExplaination: string, verifyMethod: VerifyPassword, requirementList: HTMLDivElement): void {

        const validCheck = new CjsIconFa({
            name: "fa-check",
            classes: ["text-success", MARGIN.ME_1]
        });
        setDisplay(validCheck, Display.NONE);

        const invalidCheck = new CjsIconFa({
            name: "fa-xmark",
            classes: ["text-danger", MARGIN.ME_1]
        });

        const requirementText = createHtmlElement("div", {
            innerHTML: requirementExplaination,
            cssClass: [FLEX_CONTAINER_COLUMN, FLEX_ITEM_AUTO]
        });
        const passwordRequirement = createHtmlElement("div", {
            innerHTML: [validCheck, invalidCheck, requirementText],
            cssClass: [FLEX_CONTAINER, "requirement-elements"]
        });

        requirementList.append(passwordRequirement);

        this._validationRequirements.push({ passwordRequirement, validIcon: validCheck, invalidIcon: invalidCheck, verifyMethod });
    }

    /** Check that the form is valid */
    private _arePasswordsValid(firstPasswordValue: string | undefined, secondPasswordValue: string | undefined): boolean {
        let isAllPasswordsValid = true;

        this._validationRequirements.forEach(v => {
            const isValid = v.verifyMethod(firstPasswordValue ?? "", secondPasswordValue ?? "");
            toggleVisible(v.validIcon, isValid, Display.BLOCK);
            toggleVisible(v.invalidIcon, !isValid, Display.BLOCK);

            if (!isValid) {
                isAllPasswordsValid = false;
            }
        });

        // Handle confirm button display
        if (this._confirmButton) {
            this._confirmButton.options.setAndRefreshIfChanged({ disabled: !isAllPasswordsValid });
        }

        return isAllPasswordsValid;
    }

    /**
     * Clear the visible error
     */
    private _clearError(errorContainer: HTMLDivElement): void {
        errorContainer.innerText = "";
        errorContainer.classList.remove("visible");
    }

    /**
     * Display error in _components.errorMessage
     */
    private _showError(error: ErrorWithCode, errorContainer: HTMLDivElement): Promise<void> {
        return this._errorMessageService.getErrorMessage(error).then((errorMessage) => {
            if (errorMessage) {
                errorContainer.innerText = errorMessage;
                errorContainer.classList.add("visible");
            }
        });
    }
}